
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleNavClick = () => {
    setIsMenuOpen(false);
    // Scroll to top when navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navItems = [
    { name: 'Startseite', href: '/' },
    { name: 'Über uns', href: '/uber-uns' },
    { name: 'Leistungen', href: '/leistungen' },
    { name: 'Referenzen', href: '/referenzen' },
    { name: 'Partner', href: '/partner' },
    { name: 'Kontakt', href: '/kontakt' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-elegant' 
          : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20 md:h-22 lg:h-24">
          {/* Logo with enhanced size and responsive scaling */}
          <Link to="/" className="flex items-center group" onClick={handleNavClick}>
            <img
              src="https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/logo/logo.png"
              alt="AMMING"
              className={`h-12 md:h-16 lg:h-20 w-auto group-hover:scale-105 transition-all duration-300 ${
                isScrolled 
                  ? 'brightness-0' // Black logo when scrolled
                  : 'brightness-0 invert' // White logo when transparent
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={handleNavClick}
                className={`relative font-medium transition-all duration-300 ${
                  location.pathname === item.href
                    ? isScrolled
                      ? 'text-amming-secondary after:content-[""] after:absolute after:w-full after:scale-x-100 after:h-0.5 after:bottom-0 after:left-0 after:bg-amming-secondary'
                      : 'text-white after:content-[""] after:absolute after:w-full after:scale-x-100 after:h-0.5 after:bottom-0 after:left-0 after:bg-white'
                    : isScrolled
                      ? 'text-gray-700 hover:text-amming-secondary after:content-[""] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-amming-secondary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left'
                      : 'text-white hover:opacity-80 transition-opacity duration-300'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className={`w-6 h-6 transition-colors duration-300 ${
                isScrolled ? 'text-gray-800' : 'text-white'
              }`} />
            ) : (
              <Menu className={`w-6 h-6 transition-colors duration-300 ${
                isScrolled ? 'text-gray-800' : 'text-white'
              }`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-lg shadow-elegant rounded-lg mt-2">
            <nav className="py-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-3 transition-colors hover:bg-gray-50 rounded-lg mx-2 ${
                    location.pathname === item.href
                      ? 'text-amming-secondary font-medium bg-gray-50'
                      : 'text-gray-700'
                  }`}
                  onClick={handleNavClick}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
